import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  INTEREST_STAGE_COLORS,
  LEAD_ACTIVITY_LABELS,
  LEAD_STAGE_COLORS,
  describeInquiryLinkMeta,
  describeLeadActivity,
  describeLeadActor,
  describeLeadItemActivity,
  describeManualInquiryBatchMeta,
  describeManualInquiryMeta,
  filterLeadActivityEntries,
  getManualInquiryTags,
  groupLeadActivity,
  groupLeadInterests,
  groupManualInquiryBatches,
  isLeadInquiryActivity,
  readInquiryContactDiff,
} from '../leadsConfig';
import { formatDayGroupStamp } from '../../Utils/dayGroups';
import LeadActorLine from './LeadActorLine';
import InquiryOriginTags from './InquiryOriginTags';
import {
  LEADS_BG_ELEVATED,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  LEADS_TEXT_SECONDARY,
  activityGroupLabelSx,
  leadsSectionTitleSx,
} from './leadsUi.styles';

const isBatchEntry = (entry) => entry?.action === 'inquiry_manual_added' && Array.isArray(entry.batch) && entry.batch.length > 1;

/** Hoy y Ayer ya dicen el día en el grupo: alcanza la hora (`formatDayGroupStamp`). */
const formatActivityStamp = formatDayGroupStamp;

/**
 * Timeline del lead. `filterId` (el mismo de intereses y consultas) deja solo
 * lo de ese registro. `'all'` muestra todo; notas y datos del lead quedan ahí.
 *
 * @param {{ entries?: object[], items?: object[], inquiries?: object[]|null,
 *   filterId?: string }} props
 */
export default function LeadActivity({
  entries = [],
  items = [],
  inquiries = null,
  filterId = 'all',
}) {
  const list = useMemo(() => (Array.isArray(entries) ? entries : []), [entries]);
  const { groups, others } = useMemo(
    () => groupLeadInterests(items, inquiries),
    [items, inquiries],
  );
  const visible = useMemo(
    () => filterLeadActivityEntries(list, filterId, groups, others),
    [list, filterId, groups, others],
  );
  // Carga manual con varios registros (`batch_id`) → una sola tarjeta.
  const activityGroups = useMemo(() => groupLeadActivity(groupManualInquiryBatches(visible)), [visible]);

  const emptyLabel = filterId === 'others'
    ? 'Sin actividad de estas consultas.'
    : filterId === 'all'
      ? 'Sin actividad todavía.'
      : 'Sin actividad de este interés.';

  return (
    <Box>
      <Typography sx={{ ...leadsSectionTitleSx, mb: 1 }}>Actividad</Typography>
      {activityGroups.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {activityGroups.map((group) => (
            <Box key={group.label}>
              <Typography sx={activityGroupLabelSx}>
                {group.label}
              </Typography>
              <Box component="ol" aria-label={group.label} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
                {group.entries.map((entry) => {
                  const isManualInquiry = entry.action === 'inquiry_manual_added';
                  const isInquiry = isLeadInquiryActivity(entry.action);
                  const isItemEvent = entry.action === 'item_added' || entry.action === 'item_removed';
                  const isInterestStage = entry.action === 'interest_stage_changed';
                  const isNote = entry.action === 'note_added' || (isInquiry && Boolean(entry.note));
                  const itemDetail = isItemEvent ? describeLeadItemActivity(entry, { items }) : '';
                  const detail = isItemEvent
                    ? [
                      itemDetail,
                      entry.action === 'item_removed' && entry.payload?.inquiry_id ? 'venía de una consulta' : '',
                    ].filter(Boolean).join(' · ')
                    : (isBatchEntry(entry) && !entry.note ? describeManualInquiryBatchMeta(entry.batch) : describeLeadActivity(entry, { items }));
                  const title = isManualInquiry
                    ? LEAD_ACTIVITY_LABELS.inquiry_manual_added
                    : isInquiry
                      ? (entry.payload?.link === 'manual' ? 'Consulta vinculada' : 'Consulta recibida')
                      : (LEAD_ACTIVITY_LABELS[entry.action] || entry.action);
                  // Manual: «Manual» + canal al lado del título; la línea chica dice registro / fecha real.
                  const originTags = isManualInquiry ? getManualInquiryTags(entry.payload, { manual: true }) : [];
                  const isBatch = isBatchEntry(entry);
                  const manualMeta = isBatch
                    ? describeManualInquiryBatchMeta(entry.batch)
                    : describeManualInquiryMeta(entry.payload, entry.created_at);
                  const inquiryMeta = isBatch
                    ? (entry.note ? manualMeta : '')
                    : !isInquiry || !entry.note
                      ? ''
                      : isManualInquiry ? manualMeta : describeInquiryLinkMeta(entry.payload);
                  const contactDiff = isInquiry ? readInquiryContactDiff(entry.payload) : [];
                  const actor = describeLeadActor(entry.actor);
                  const dotColor = entry.action === 'stage_changed'
                    ? (LEAD_STAGE_COLORS[entry.payload?.to] || '#6DA8A7')
                    : isInterestStage ? (INTEREST_STAGE_COLORS[entry.payload?.to] || '#6DA8A7')
                      : (isInquiry || entry.action === 'inquiry_record_assigned') ? '#489291' : isNote ? '#E3B04B' : 'rgba(255,255,255,0.3)';
                  return (
                    <Box
                      component="li"
                      key={entry.id}
                      sx={{
                        position: 'relative',
                        pl: 2.25,
                        pb: 1.5,
                        borderLeft: '1px solid rgba(255,255,255,0.10)',
                        ml: '4px',
                        '&:last-of-type': { borderLeftColor: 'transparent', pb: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -5,
                          top: 4,
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          bgcolor: dotColor,
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: LEADS_TEXT_PRIMARY }}>{title}</Typography>
                          <InquiryOriginTags tags={originTags} />
                        </Box>
                        <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED, whiteSpace: 'nowrap' }}>
                          {formatActivityStamp(entry.created_at, group.label)}
                        </Typography>
                      </Box>
                      <LeadActorLine actor={actor} sx={{ mt: 0.125 }} />
                      {detail && detail !== title && (
                        <Typography
                          sx={{
                            mt: 0.25,
                            fontSize: '13px',
                            color: isNote ? LEADS_TEXT_PRIMARY : LEADS_TEXT_SECONDARY,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            ...(isNote ? { bgcolor: LEADS_BG_ELEVATED, borderRadius: '8px', px: 1.25, py: 0.75, mt: 0.5 } : {}),
                          }}
                        >
                          {detail}
                        </Typography>
                      )}
                      {inquiryMeta && (
                        <Typography sx={{ mt: 0.5, fontSize: '11px', color: LEADS_TEXT_MUTED }}>{inquiryMeta}</Typography>
                      )}
                      {contactDiff.length > 0 && (
                        <Typography sx={{ mt: 0.5, fontSize: '11px', color: LEADS_TEXT_SECONDARY }}>
                          {'La consulta trajo otros datos: '}
                          {contactDiff.map((d) => `${d.label} “${d.inquiry}”${d.lead ? ` (acá: ${d.lead})` : ''}`).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: '12px', color: LEADS_TEXT_MUTED }}>{emptyLabel}</Typography>
      )}
    </Box>
  );
}
